import { CheckCircle as CheckCircleOutline, Close } from "@mui/icons-material";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { green } from "@mui/material/colors";

import React from "react";
import { getFileIconAndColor } from "src/reusable/Functions";
import { formatFileSize } from "src/helpers/formatFileSize";
import { useProcessingStatus } from "src/reusable/hooks/useProcessingStatus";
import { FileWithPreview } from "../types";
import { ChunkedFileWithPreview } from "../types/chunkedUploadTypes";
import AddToProjectButton from "../AssistantMessage/artifacts/AddToProjectButton";

type AttachmentListItemProps = {
  file: FileWithPreview | ChunkedFileWithPreview;
  fileId: string;
  onRemove?: (fileId: string) => void; // Changed from index to fileId
  isEditable?: boolean;
  onStatusChange?: (fileId: string, isReady: boolean) => void;
  // Set when the artifact ID fetch is pending ("finalizing") or exhausted ("failed").
  pendingArtifact?: "finalizing" | "failed" | false;
  // True when the surrounding dialogue is a project chat (has a project
  // folder). The ad-hoc upload "Add to Project" surface only renders in
  // project chats; in a general chat it is intentionally absent.
  isProjectChat?: boolean;
};

const AttachmentListItem: React.FC<AttachmentListItemProps> = ({
  file,
  fileId,
  onRemove,
  isEditable = false,
  onStatusChange,
  pendingArtifact,
  isProjectChat = false,
}) => {
  // Get IDs from file object
  const datasetId = (file as any).datasetId;
  const sourceDataRecordId = (file as any).sourceDataRecordId;
  const workspacePath = (file as any).workspacePath;
  const origin = (file as any).origin as
    | "dialogue"
    | "dataset"
    | "project"
    | "workspace"
    | undefined;
  // Workspace uploads bypass the dataset pipeline. `origin` is authoritative;
  // `workspacePath` stays a fallback for legacy workspace-direct uploads. A
  // file that carries a dataset/SDR id is never a workspace upload, so a Ready
  // dataset-backed file is no longer misclassified by the absence of ids (the
  // old `isReady` inference that created the gating dead zone).
  const isWorkspaceUpload =
    (origin === "workspace" || !!workspacePath) &&
    !datasetId &&
    !sourceDataRecordId;

  // Use React Query to fetch processing status directly from API.
  // Skip polling entirely for workspace uploads (no dataset pipeline).
  // Always enabled during pendingArtifact so the result is cached and
  // instantly available the moment pendingArtifact flips off.
  const { data: processingStatus } = useProcessingStatus({
    sdrId: sourceDataRecordId,
    datasetId: !sourceDataRecordId ? datasetId : undefined,
    filename: !sourceDataRecordId ? file.name : undefined,
    enabled: !isWorkspaceUpload && !!(sourceDataRecordId || datasetId),
  });

  // Notify parent of status changes
  React.useEffect(() => {
    if (onStatusChange) {
      if (isWorkspaceUpload) {
        onStatusChange(fileId, true);
      } else if (processingStatus) {
        const isReady =
          processingStatus.display_status === "Ready" &&
          !processingStatus.is_processing;
        onStatusChange(fileId, isReady);
      }
    }
  }, [processingStatus, fileId, onStatusChange, isWorkspaceUpload]);

  // Determine artifact-fetch override state:
  // "finalizing" — processing done but artifact ID not yet available (show spinner)
  // "failed" — retries exhausted, artifact ID never became available (show error)
  // Only override display when the backend confirms processing is actually done.
  // Workspace uploads skip the dataset pipeline entirely.
  const artifactFailed = !isWorkspaceUpload && pendingArtifact === "failed";
  const isFinalizing =
    !isWorkspaceUpload &&
    pendingArtifact === "finalizing" &&
    !processingStatus?.is_processing;
  const chunkedFile = file as ChunkedFileWithPreview;
  // A failed upload has no processing status to poll, so its state has to
  // come from the upload hook's own flag.
  const uploadFailed = chunkedFile.uploadStatus === "failed";
  const uploadError = chunkedFile.error;

  let statusText: string;
  if (isWorkspaceUpload) {
    statusText = "Ready";
  } else if (uploadFailed) {
    statusText = "Upload failed";
  } else if (artifactFailed) {
    statusText = "Failed";
  } else if (isFinalizing) {
    statusText = "Finalizing...";
  } else {
    statusText = processingStatus?.display_status || "Waiting for resources...";
  }

  const showSpinner =
    !isWorkspaceUpload && (isFinalizing || processingStatus?.is_processing);

  const progressValue = isWorkspaceUpload
    ? 100
    : (processingStatus?.progress_percentage ?? 0);
  const showProgress =
    !isWorkspaceUpload &&
    !isFinalizing &&
    !artifactFailed &&
    processingStatus?.is_processing &&
    progressValue > 0;

  // Color based on display status
  let statusColor:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning" = "default";
  if (isFinalizing) {
    statusColor = "secondary";
  } else if (statusText === "Ready") {
    statusColor = "success";
  } else if (artifactFailed || uploadFailed) {
    statusColor = "error";
  } else if (processingStatus?.is_processing) {
    statusColor = "secondary";
  }

  const { icon } = getFileIconAndColor({ fileName: file.name, fontSize: 16 });

  let sizeFormatted: string | null = null;
  if (
    chunkedFile.sizeFormatted &&
    chunkedFile.sizeFormatted !== "Unknown size"
  ) {
    sizeFormatted = chunkedFile.sizeFormatted;
  } else if (file.size && file.size > 0) {
    sizeFormatted = formatFileSize(file.size);
  }

  const fileDescription = (file as any).description || null;
  // Filename already shows the extension — meta slot is just the size.
  const metaText = sizeFormatted || "";

  const tooltipContent = (
    <Box>
      <Typography variant="body2" fontWeight="bold">
        {file.name}
      </Typography>
      {fileDescription && (
        <Typography variant="caption" display="block" fontStyle="italic">
          {fileDescription}
        </Typography>
      )}
      {uploadFailed && uploadError && (
        <Typography variant="caption" display="block" color="error.light">
          {uploadError}
        </Typography>
      )}
      {processingStatus && processingStatus.num_chunks > 0 && (
        <Typography variant="caption" display="block">
          Chunks: {processingStatus.num_chunks_embedded}/
          {processingStatus.num_chunks} indexed
        </Typography>
      )}
    </Box>
  );

  const fileIdentifier = fileId;
  const isReadyState = statusText === "Ready";
  // The ad-hoc upload "Add to Project" surface is project-chat-only. In a
  // general chat neither the live button nor the placeholder renders.
  const canAddToProject = isProjectChat && isReadyState && !!sourceDataRecordId;
  // Ad-hoc uploads still indexing don't yet have a sourceDataRecordId, so the
  // live "Add to Project" button can't fire. In a project chat, show a
  // disabled stand-in with a tooltip so users know the action exists once
  // indexing finishes (spec §7.1). Together with canAddToProject this
  // guarantees a Ready dataset-backed attachment in a project chat always
  // renders button-or-placeholder, never nothing.
  const showIndexingPlaceholder =
    isProjectChat &&
    !canAddToProject &&
    !isWorkspaceUpload &&
    !artifactFailed &&
    !uploadFailed &&
    !sourceDataRecordId;

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="bottom-start"
      enterDelay={500}
    >
      <Box
        data-testid={`aa-file-item-${fileIdentifier}`}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          maxWidth: "100%",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "action.hover",
          },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
          >
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
              {icon}
            </Box>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {file.name}
            </Typography>

            {metaText && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              >
                {metaText}
              </Typography>
            )}
          </Stack>

          {canAddToProject && (
            <Box sx={{ flexShrink: 0 }}>
              <AddToProjectButton
                sdrId={sourceDataRecordId}
                filename={file.name}
              />
            </Box>
          )}
          {showIndexingPlaceholder && (
            <Box sx={{ flexShrink: 0 }}>
              <Tooltip title="Available once indexing finishes." arrow>
                {/* Wrap in span so the Tooltip still fires while the button is
                    disabled (MUI requirement). */}
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled
                    startIcon={<AddToPhotosIcon />}
                    sx={{ minWidth: 140 }}
                  >
                    Add to Project
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}

          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
            {isReadyState ? (
              <Tooltip title="Ready" arrow>
                <CheckCircleOutline
                  data-testid={`aa-file-status-${fileIdentifier}`}
                  sx={{
                    fontSize: 16,
                    color: green[500],
                    opacity: 0.8,
                  }}
                />
              </Tooltip>
            ) : (
              <Chip
                data-testid={`aa-file-status-${fileIdentifier}`}
                label={statusText}
                size="small"
                color={statusColor}
                variant="filled"
                icon={
                  showSpinner ? (
                    <CircularProgress size={10} sx={{ color: "inherit" }} />
                  ) : undefined
                }
                sx={{
                  height: 20,
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  borderRadius: "10px",
                  "& .MuiChip-label": { px: 1 },
                  "& .MuiChip-icon": { ml: 0.5, mr: -0.5 },
                }}
              />
            )}
          </Box>

          {isEditable && onRemove && (
            <IconButton
              data-testid={`aa-file-remove-${fileIdentifier}`}
              data-attr="assistant:btn:remove-attachment"
              size="small"
              onClick={() => {
                const fileId =
                  (file as any).uniqueId || (file as any).id || file.name;
                onRemove(fileId);
              }}
              sx={{
                flexShrink: 0,
                p: 0.25,
                opacity: 0.7,
                "&:hover": {
                  opacity: 1,
                  color: "error.main",
                },
              }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>

        {showProgress && (
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              mt: 0.5,
              height: 3,
              borderRadius: 1.5,
              backgroundColor: "action.hover",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "secondary.main",
                borderRadius: 1.5,
              },
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export default AttachmentListItem;
